import {
  getGetFlowRunQueryKey,
  getListWorkspaceFlowRunsQueryKey,
  useCancelFlowRun,
  useGetFlowRun,
  useStartFlowRun,
} from '@reelplicate/api-client/flow-runs';
import { getListFlowsQueryKey } from '@reelplicate/api-client/flows';
import type { FlowDto, FlowRunDto, FlowRunMode } from '@reelplicate/api-client/model';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router';

import { isOpen, RUN_POLL_MS, RunContext, type RunState } from './run-state';

/**
 * Keeps the editor's current run: the flow's latest run on load, then whatever
 * Test run / Run flow starts. Polls while it's open.
 */
export function RunProvider({
  workspaceId,
  flow,
  beforeStart,
  children,
}: {
  workspaceId: string;
  flow: FlowDto;
  /** Saves the draft first; false stops the run from starting. */
  beforeStart: () => Promise<boolean>;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  // `?run=` opens a given run (from the Running page); otherwise the latest.
  const [searchParams] = useSearchParams();
  const [runId, setRunId] = useState(searchParams.get('run') ?? flow.lastRun?.id);
  const { data: run } = useGetFlowRun(workspaceId, runId ?? '', {
    query: {
      enabled: Boolean(runId),
      throwOnError: false,
      refetchInterval: (query) => (isOpen(query.state.data) ? RUN_POLL_MS : false),
    },
  });
  const startRun = useStartFlowRun();
  const cancelRun = useCancelFlowRun();
  const [starting, setStarting] = useState<FlowRunMode | null>(null);

  const show = (next: FlowRunDto) => {
    queryClient.setQueryData(getGetFlowRunQueryKey(workspaceId, next.id), next);
    setRunId(next.id);
  };

  const state: RunState = {
    workspaceId,
    run,
    starting,
    show,
    start: async (mode) => {
      setStarting(mode);
      try {
        if (!(await beforeStart())) {
          toast.error('Save the flow first', {
            description: 'The latest edits couldn’t be saved.',
          });
          return;
        }
        show(
          await startRun.mutateAsync({
            id: workspaceId,
            flowId: flow.id,
            data: { mode },
          }),
        );
        void queryClient.invalidateQueries({
          queryKey: getListFlowsQueryKey(workspaceId),
        });
        // The sidebar's Running count.
        void queryClient.invalidateQueries({
          queryKey: getListWorkspaceFlowRunsQueryKey(workspaceId),
          exact: true,
        });
      } catch {
        return; // The refusal (errors, nodes that can't run yet) is already a toast.
      } finally {
        setStarting(null);
      }
    },
    cancel: async () => {
      if (!run) return;
      try {
        show(await cancelRun.mutateAsync({ id: workspaceId, runId: run.id }));
        toast('Run stopped');
      } catch {
        return;
      }
    },
  };
  return <RunContext value={state}>{children}</RunContext>;
}
